import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IonButton,
  IonSpinner,
  IonIcon,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import {
  syncOutline,
  alertCircleOutline,
  bugOutline,
  warningOutline,
  informationCircleOutline,
  skullOutline,
  closeCircleOutline,
} from 'ionicons/icons';
import api from '../../services/api';
import { ExpandableSearch, ActionAlert, SyncingBadge, SkeletonTable } from '../../components/atoms';
import { useIssuesSearch, useSelectedTickets } from '../ProjectsPage/ProjectLayout';
import '../TicketsPage/TicketsPage.css';
import './SentryIssuesPage.css';

const getLevelIcon = (level) => {
  switch (level) {
    case 'fatal': return skullOutline;
    case 'error': return alertCircleOutline;
    case 'warning': return warningOutline;
    case 'info': return informationCircleOutline;
    default: return bugOutline;
  }
};

const getLevelColor = (level) => {
  switch (level) {
    case 'fatal': return '#ff4d4f';
    case 'error': return '#f87168';
    case 'warning': return '#faad14';
    case 'info': return '#579dff';
    default: return '#6b778c';
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical': return '#ff4d4f';
    case 'high': return '#f87168';
    case 'medium': return '#faad14';
    case 'low': return '#52c41a';
    default: return '#6b778c';
  }
};

const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatCount = (count) => {
  const num = parseInt(count, 10);
  if (isNaN(num)) return count;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return String(num);
};

const SentryIssuesPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { issuesSearchQuery: searchQuery, setIssuesSearchQuery: setSearchQuery } = useIssuesSearch();
  const { selectedTickets: selectedIssues, setSelectedTickets: setSelectedIssues } = useSelectedTickets();

  const [issues, setIssues] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // Sentry filters — restore from localStorage
  const storageKey = `sentry-filters-${projectId}`;
  const savedFilters = JSON.parse(localStorage.getItem(storageKey) || '{}');

  const [sentryProjects, setSentryProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(savedFilters.project || '');
  const [selectedEnvironment, setSelectedEnvironment] = useState(savedFilters.environment || '');
  const [sortBy, setSortBy] = useState(savedFilters.sortBy || 'date');
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const environments = ['production', 'staging', 'development'];

  // Persist filter selections to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({
      project: selectedProject,
      environment: selectedEnvironment,
      sortBy,
    }));
  }, [selectedProject, selectedEnvironment, sortBy, storageKey]);

  useEffect(() => {
    loadSentryProjects();
  }, []);

  useEffect(() => {
    loadIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, selectedEnvironment, sortBy]);

  const loadSentryProjects = async () => {
    try {
      const data = await api.get('/sentry/projects');
      setSentryProjects(data.projects || []);
    } catch (err) {
      console.error('Failed to load Sentry projects:', err);
    }
  };

  const loadIssues = useCallback(async (isRefresh = false) => {
    const minDelay = new Promise(r => setTimeout(r, 600));
    try {
      setSyncing(true);
      setError(null);

      const params = new URLSearchParams();
      if (selectedProject) params.set('project', selectedProject);
      if (selectedEnvironment) params.set('environment', selectedEnvironment);
      if (searchQuery) params.set('query', searchQuery);
      params.set('sort', sortBy);

      const [data] = await Promise.all([api.get(`/sentry/issues?${params.toString()}`), minDelay]);
      setIssues(data.issues || []);
      setNextCursor(data.nextCursor || null);
      sessionStorage.removeItem('sentry_integration_error');
    } catch (err) {
      await minDelay;
      console.error('Error loading Sentry issues:', err);
      const msg = err.message || 'Failed to load issues';
      setError(msg);
      if (msg.includes('not connected') || msg.includes('not active')) {
        sessionStorage.setItem('sentry_integration_error', msg);
      }
    } finally {
      setInitialLoading(false);
      setSyncing(false);
    }
  }, [selectedProject, selectedEnvironment, searchQuery, sortBy]);

  const loadMoreIssues = async () => {
    if (!nextCursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const params = new URLSearchParams();
      if (selectedProject) params.set('project', selectedProject);
      if (selectedEnvironment) params.set('environment', selectedEnvironment);
      if (searchQuery) params.set('query', searchQuery);
      params.set('sort', sortBy);
      params.set('cursor', nextCursor);

      const data = await api.get(`/sentry/issues?${params.toString()}`);
      setIssues(prev => [...prev, ...(data.issues || [])]);
      setNextCursor(data.nextCursor || null);
    } catch (err) {
      console.error('Error loading more issues:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleIssueClick = (e, issueId) => {
    if (e.target.type === 'checkbox') return;
    navigate(`/projects/${projectId}/issues/${issueId}`);
  };

  const handleSelectIssue = (issueShortId, checked) => {
    setSelectedIssues(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(issueShortId);
      } else {
        newSet.delete(issueShortId);
      }
      return newSet;
    });
  };

  const clearSelection = () => {
    setSelectedIssues(new Set());
  };

  const isIntegrationError = error && (error.includes('not connected') || error.includes('not active'));

  if (isIntegrationError) {
    return (
      <div className="tickets-error">
        <ActionAlert
          title="Sentry Not Connected"
          description="Connect Sentry to view issues."
          actionLabel="Connect"
          actionTo="/integrations"
          fadeIn
        />
      </div>
    );
  }

  if (initialLoading || (syncing && issues.length === 0 && !error)) {
    return <SkeletonTable overlay={<SyncingBadge label="Syncing" />} />;
  }

  return (
    <div className="tickets-page-wrapper breadcrumb-spacing">
      <div className="tickets-page">
        {/* Filters */}
        <div className="tickets-filters">
          <div className="status-filters">
            {/* Sentry Project Selector */}
            {sentryProjects.length > 0 && (
              <IonSelect
                value={selectedProject}
                onIonChange={(e) => setSelectedProject(e.detail.value)}
                interface="popover"
                className="status-dropdown"
                placeholder="All Projects"
              >
                <IonSelectOption value="">All Projects</IonSelectOption>
                {sentryProjects.map(p => (
                  <IonSelectOption key={p.id} value={p.id}>
                    {p.slug}
                  </IonSelectOption>
                ))}
              </IonSelect>
            )}

            {/* Environment Selector */}
            <IonSelect
              value={selectedEnvironment}
              onIonChange={(e) => setSelectedEnvironment(e.detail.value)}
              interface="popover"
              className="status-dropdown"
              placeholder="All Environments"
            >
              <IonSelectOption value="">All Environments</IonSelectOption>
              {environments.map(env => (
                <IonSelectOption key={env} value={env}>
                  {env}
                </IonSelectOption>
              ))}
            </IonSelect>

            {/* Sort */}
            <IonSelect
              value={sortBy}
              onIonChange={(e) => setSortBy(e.detail.value)}
              interface="popover"
              className="status-dropdown"
            >
              <IonSelectOption value="date">Last Seen</IonSelectOption>
              <IonSelectOption value="new">First Seen</IonSelectOption>
              <IonSelectOption value="freq">Events</IonSelectOption>
              <IonSelectOption value="user">Users</IonSelectOption>
              <IonSelectOption value="trends">Trends</IonSelectOption>
            </IonSelect>

            {selectedIssues.size > 0 && (
              <>
                <span className="selection-count">
                  {selectedIssues.size} selected
                </span>
                <button
                  className="sync-button"
                  onClick={clearSelection}
                  title="Clear selection"
                >
                  <IonIcon icon={closeCircleOutline} />
                </button>
              </>
            )}

            {selectedIssues.size === 0 && (
              <button
                className={`sync-button ${syncing ? 'syncing' : ''}`}
                onClick={() => loadIssues(true)}
                disabled={syncing}
                title="Refresh issues"
              >
                <IonIcon icon={syncOutline} />
              </button>
            )}
          </div>

          <div className="tickets-filters-right">
            <ExpandableSearch
              placeholder="Search issues..."
              value={searchQuery || ''}
              onChange={(val) => setSearchQuery(val)}
              debounce={500}
            />
          </div>
        </div>

        {/* Issues List */}
        {error ? (
          <div className="tickets-error">
            <IonIcon icon={alertCircleOutline} />
            <p>{error}</p>
            <IonButton onClick={() => loadIssues()}>Retry</IonButton>
          </div>
        ) : syncing ? (
          <div className="tickets-loading">
            <IonSpinner />
          </div>
        ) : issues.length === 0 ? (
          <div className="tickets-empty">
            <IonIcon icon={bugOutline} />
            <h2>No issues found</h2>
            <p>
              {searchQuery || selectedProject || selectedEnvironment
                ? 'Try adjusting your filters'
                : 'No Sentry issues found. Make sure Sentry is connected in Integrations.'}
            </p>
          </div>
        ) : (
          <>
            <table className="tickets-table sentry-issues-table">
              <thead>
                <tr>
                  <th className="col-checkbox">
                    <input
                      type="checkbox"
                      className="sprint-checkbox"
                      checked={issues.length > 0 && issues.every(i => selectedIssues.has(i.shortId))}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedIssues(() => {
                          const newSet = new Set();
                          if (checked) {
                            issues.forEach(i => newSet.add(i.shortId));
                          }
                          return newSet;
                        });
                      }}
                    />
                  </th>
                  <th className="col-level"></th>
                  <th className="col-issue">Issue</th>
                  <th className="col-events">Events</th>
                  <th className="col-users">Users</th>
                  <th className="col-priority">Priority</th>
                  <th className="col-last-seen">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <tr
                    key={issue.id}
                    className="ticket-row"
                    onClick={(e) => handleIssueClick(e, issue.id)}
                  >
                    <td className="col-checkbox" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIssues.has(issue.shortId)}
                        onChange={(e) => handleSelectIssue(issue.shortId, e.target.checked)}
                      />
                    </td>
                    <td className="col-level">
                      <IonIcon
                        icon={getLevelIcon(issue.level)}
                        style={{ color: getLevelColor(issue.level) }}
                        title={issue.level}
                      />
                    </td>
                    <td className="col-summary">
                      <div className="issue-main">
                        <span className="issue-short-id">{issue.shortId}</span>
                        <span className="issue-title">{issue.title}</span>
                      </div>
                      <div className="issue-meta">
                        <span className="issue-culprit">{issue.culprit}</span>
                        {issue.project && (
                          <span className="issue-project">{issue.project.slug}</span>
                        )}
                      </div>
                    </td>
                    <td className="col-events">
                      <span className="count-badge">{formatCount(issue.count)}</span>
                    </td>
                    <td className="col-users">
                      <span className="count-badge">{formatCount(issue.userCount)}</span>
                    </td>
                    <td className="col-priority">
                      {issue.priority && (
                        <span
                          className="priority-badge"
                          style={{ color: getPriorityColor(issue.priority) }}
                        >
                          {issue.priority}
                        </span>
                      )}
                    </td>
                    <td className="col-last-seen">
                      <span className="time-text">{formatRelativeTime(issue.lastSeen)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {nextCursor && (
              <div className="sentry-load-more">
                <IonButton
                  fill="clear"
                  onClick={loadMoreIssues}
                  disabled={loadingMore}
                >
                  {loadingMore ? <IonSpinner name="crescent" /> : 'Load More'}
                </IonButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SentryIssuesPage;
